import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Mail, Send, RefreshCw, TrendingUp, Database, CheckCircle } from 'lucide-react';

const API_URL = '/api';

const Dashboard = () => {
  const [stats, setStats] = useState({ received: 0, sent: 0 });
  const [followupStats, setFollowupStats] = useState({
    total_sent: 0,
    responses: 0,
    followups_sent: 0,
    pending_followups: 0,
    response_rate: 0
  });
  const [isGmailConnected, setIsGmailConnected] = useState(false);

  useEffect(() => {
    loadStats();
    loadFollowupStats();
    checkGmailStatus();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/stats`);
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadFollowupStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/followup/stats`);
      if (response.data.success) {
        setFollowupStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load follow-up stats:', error);
    }
  };

  const checkGmailStatus = () => {
    const saved = localStorage.getItem('gmailEmail');
    setIsGmailConnected(!!saved);
  };

  return (
    <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <Header 
        title="Dashboard" 
        subtitle="Overview of your email automation system"
        stats={stats}
      />

      <div className="p-8">
        {/* Status Banner */}
        <Card className="mb-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">Welcome to Email Automation!</h2>
                <p className="text-blue-100">
                  {isGmailConnected 
                    ? '✅ Gmail Connected - System Ready'
                    : '⚠️ Please connect Gmail to get started'}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold">{stats.received.toLocaleString()}</div>
                  <div className="text-sm text-blue-100">Total Emails</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Received Emails */}
          <Card className="border-blue-200 hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-600" />
                Received Emails
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{stats.received.toLocaleString()}</div>
              <p className="text-xs text-gray-500 mt-2">Ready for replies</p>
            </CardContent>
          </Card>

          {/* Sent Emails */}
          <Card className="border-green-200 hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Send className="h-4 w-4 text-green-600" />
                Sent Emails
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{stats.sent}</div>
              <p className="text-xs text-gray-500 mt-2">Total sent</p>
            </CardContent>
          </Card>

          {/* Follow-ups */}
          <Card className="border-purple-200 hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-purple-600" />
                Follow-ups
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">{followupStats.followups_sent}</div>
              <p className="text-xs text-gray-500 mt-2">{followupStats.pending_followups} pending</p>
            </CardContent>
          </Card>

          {/* Response Rate */}
          <Card className="border-orange-200 hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-orange-600" />
                Response Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{followupStats.response_rate}%</div>
              <p className="text-xs text-gray-500 mt-2">{followupStats.responses} responses</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions & Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-blue-600" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <a
                href="/reply"
                className="block p-4 bg-gradient-to-r from-cyan-50 to-blue-50 hover:from-cyan-100 hover:to-blue-100 rounded-lg transition-colors border border-cyan-200"
              >
                <div className="flex items-center gap-3">
                  <Mail className="h-8 w-8 text-cyan-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Reply to Emails</div>
                    <div className="text-sm text-gray-500">Generate AI replies</div>
                  </div>
                </div>
              </a>

              <a
                href="/follow-ups"
                className="block p-4 bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 rounded-lg transition-colors border border-indigo-200"
              >
                <div className="flex items-center gap-3">
                  <RefreshCw className="h-8 w-8 text-indigo-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Follow-up System</div>
                    <div className="text-sm text-gray-500">Check & generate follow-ups</div>
                  </div>
                </div>
              </a>

              <a
                href="/generate"
                className="block p-4 bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 rounded-lg transition-colors border border-purple-200"
              >
                <div className="flex items-center gap-3">
                  <Mail className="h-8 w-8 text-purple-600" />
                  <div>
                    <div className="font-semibold text-gray-900">Generate Emails</div>
                    <div className="text-sm text-gray-500">Create new emails with AI</div>
                  </div>
                </div>
              </a>
            </CardContent>
          </Card>

          {/* System Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-green-600" />
                System Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Gmail Status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`h-3 w-3 rounded-full ${isGmailConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="font-medium text-gray-700">Gmail Connection</span>
                </div>
                <Badge variant={isGmailConnected ? "default" : "destructive"}>
                  {isGmailConnected ? 'Connected' : 'Disconnected'}
                </Badge>
              </div>

              {/* Database Status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full bg-green-500"></div>
                  <span className="font-medium text-gray-700">Database</span>
                </div>
                <Badge variant="default">Active</Badge>
              </div>

              {/* AI System */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full bg-green-500"></div>
                  <span className="font-medium text-gray-700">AI System</span>
                </div>
                <Badge variant="default">Ready</Badge>
              </div>

              {/* Follow-up Tracker */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full bg-green-500"></div>
                  <span className="font-medium text-gray-700">Follow-up Tracker</span>
                </div>
                <Badge variant="default">Active</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

